import { GoogleLogin } from '@react-oauth/google';
import { joiResolver } from '@hookform/resolvers/joi';
import type { AxiosError } from 'axios';
import googleSigninIcon from 'assets/icons/google_signin.png';
import microsoftSigninIcon from 'assets/icons/microsoftIcon.svg';
import { useMsal } from '@azure/msal-react';
import { getMicrosoftIdToken } from 'auth/getMicrosoftIdToken';
import queryString from 'query-string';
import type { FC } from 'react';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation } from 'react-router';
import useCustomNavigate from 'hooks/useCustomeNavigate';
import { toast, type ToastOptions } from 'react-toastify';
import { AlertV2 } from 'components/atoms/AlertV2';
import { ExclamationCircleIcon as ExclamationCircleIconSolid } from '@heroicons/react/solid';

import {
  loginPasswordValidationSchema,
  loginValidationSchema,
} from 'applications/expert/validation/login';

import ArrowRightIcon from '@heroicons/react/solid/ArrowRightIcon';
import { CheckIcon } from '@heroicons/react/solid';
import ExpertGlasses from 'assets/svg/icons/ExpertGlasses';
import RocketArrow from 'assets/svg/icons/RocketArrow';
import AccountableLogo from 'components/atoms/AccountableLogo';
import { Button } from 'components/atoms/Button';
import { FieldConnector } from 'components/atoms/Form/FieldConnector';
import FreeTextInput from 'components/atoms/Form/FreeTextInput';
import Link from 'components/atoms/Link';
import SignInMethodButton from 'components/atoms/SignInMethodButton';
import { Title } from 'components/atoms/Title';
import { SizesEnum } from 'components/atoms/Title/Title.enum';
import ExpertAuthBackButton from 'components/atoms/ExpertAuthBackButton';
import ExpertAuthFooter from 'components/molecules/ExpertAuthFooter.component';
import ExpertEmailFieldWithSuggestions from 'components/molecules/ExpertEmailFieldWithSuggestions';
import ExpertLanguageDropdown from 'components/molecules/ExpertLanguageDropdown.component';
import TestimonialsSlider from 'components/organisms/TestimonialsSlider';
import AuthPageLayout from 'components/templates/ExpertAuthPageLayout';
import useExpertSendMagicLink from 'hooks/Authentication/useExpertSendMagicLink';
import useExpertSignin from 'hooks/Authentication/useExpertSignin';
import { useExpertSigninSuccess } from 'hooks/Authentication/useExpertSigninSuccess';
import {
  getStoredExpertEmails,
  storeExpertEmail,
} from 'hooks/Authentication/useStoredExpertEmails';
import useLocalizedTestimonials from 'hooks/useLocalizedTestimonials';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import { handleMfaRequired } from 'utils/expertAuth';
import { getTracingEvents } from 'utils/amplitude';
import {
  WEBAPP_LOGIN_ACCOUNTANT_CLICKEDCREATEACCOUNTLINK,
  WEBAPP_LOGIN_ACCOUNTANT_CLICKEDLOGIN,
  WEBAPP_LOGIN_ACCOUNTANT_CLICKEDLOGINEMAILFIELD,
} from 'utils/amplitude/events/login.amplitude';
import { emailRegex } from 'utils/helpers';
import { EXPERT_ROUTES } from 'applications/expert/routing/expertRoutes';

const toastConfig: ToastOptions = {
  position: toast.POSITION.BOTTOM_LEFT,
  autoClose: 10000,
  hideProgressBar: true,
  pauseOnHover: true,
  closeButton: false,
};

const tracingEvents = getTracingEvents({
  onLogin: {
    name: WEBAPP_LOGIN_ACCOUNTANT_CLICKEDLOGIN,
    data: (email?: string, loginMethod?: string) => ({
      ...(email ? { email } : {}),
      login_method: loginMethod,
    }),
  },
  onClickCreateAccount: WEBAPP_LOGIN_ACCOUNTANT_CLICKEDCREATEACCOUNTLINK,
});

type LoginMode = 'methods' | 'password' | 'magic';

type LoginFormValues = {
  email: string;
  password?: string;
};

type LoginMethodsViewProps = {
  onSelectPassword: () => void;
  onSelectMagicLink: () => void;
  onGoogleLogin: (credential?: string) => void;
  onMicrosoftLogin?: () => void;
};

const LoginMethodsView: FC<LoginMethodsViewProps> = ({
  onSelectPassword,
  onSelectMagicLink,
  onGoogleLogin,
  onMicrosoftLogin,
}) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col justify-between min-h-[500px]">
      <div className="w-full max-w-md mx-auto">
        <div className="mt-6 flex flex-col gap-3">
          <Button
            color="taxes"
            type="button"
            iconPlacement="right"
            className="!w-full h-12 !text-base"
            onClick={onSelectPassword}
          >
            {t('login.expert.sign_in_with_password')}
            <ArrowRightIcon className=" ml-2 w-4 h-4 " />
          </Button>
          <Button
            structure="secondary"
            color="primary"
            type="button"
            className="!w-full h-12 !text-base"
            onClick={onSelectMagicLink}
          >
            {t('login.expert.sign_in_with_magic_link')}
            <ArrowRightIcon className="ml-2 w-4 h-4 " />
          </Button>
        </div>
        <div className="mt-6 mb-4 flex items-center">
          <div className="h-[1px] bg-primary-100 flex-1" />
          <span className="px-3 text-primary-500 font-semibold uppercase">
            {t('or')}
          </span>
          <div className="h-[1px] bg-primary-100 flex-1" />
        </div>
        <div className="flex flex-col gap-3">
          {onMicrosoftLogin && (
            <SignInMethodButton
              label={t('login.expert.sign_in_with_microsoft')}
              icon={
                <img
                  src={microsoftSigninIcon}
                  alt="Microsoft"
                  className="w-5 h-5"
                />
              }
              onClick={onMicrosoftLogin}
            />
          )}
          <div className="relative">
            <SignInMethodButton
              label={t('login.sso.google')}
              icon={
                <img src={googleSigninIcon} alt="Google" className="w-5 h-5" />
              }
            />
            <div className="absolute inset-0 flex justify-center opacity-0">
              <GoogleLogin
                shape="rectangular"
                onSuccess={(credentialResponse) =>
                  onGoogleLogin(credentialResponse?.credential)
                }
                width="400"
              />
            </div>
          </div>
        </div>
        <div className="mt-4 flex flex-col gap-8">
          <div className="flex justify-center items-center gap-2">
            <p className="text-primary-600 font-semibold whitespace-nowrap !text-base">
              {t('login.no_account_yet')}
            </p>
            <Link
              href={EXPERT_ROUTES.SIGNUP}
              className="cursor-pointer !font-normal flex whitespace-nowrap !text-base items-center gap-2 pb-0 mb-0 !text-taxes"
              onClick={() => tracingEvents.onClickCreateAccount()}
            >
              {t('login.sign_up')}
              <ArrowRightIcon className="w-4 h-4 " />
            </Link>
          </div>
        </div>
      </div>

      <div className="w-full flex flex-wrap items-center justify-center gap-6 text-sm text-primary-600 mt-8">
        {[
          t('login.expert.feature.unlimited_free_access'),
          t('login.expert.feature.collaborate_with_clients'),
          t('login.expert.feature.complete_solution'),
          t('login.expert.feature.sync_with_software'),
        ].map((label) => (
          <div key={label} className="flex items-center gap-2">
            <span className="inline-flex h-6 w-6 items-center justify-center rounded-full  text-primary flex-shrink-0 bg-taxes-100">
              <CheckIcon className="h-3 w-3" />
            </span>
            <span className="whitespace-nowrap">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

type LoginPasswordViewProps = {
  onSubmit: () => void;
  isLoading: boolean;
  storedEmails: string[];
};

const LoginPasswordView: FC<LoginPasswordViewProps> = ({
  onSubmit,
  isLoading,
  storedEmails,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="my-6">
        <ExpertEmailFieldWithSuggestions
          storedEmails={storedEmails}
          tracing={{ onFocus: WEBAPP_LOGIN_ACCOUNTANT_CLICKEDLOGINEMAILFIELD }}
        />
      </div>

      <FieldConnector label={t('login.password')} name="password">
        {({ field }) => (
          <FreeTextInput
            {...field}
            type="password"
            placeholder={t('login.password_placeholder')}
          />
        )}
      </FieldConnector>

      <div className="mt-6 flex flex-col gap-3">
        <Button
          color="taxes"
          type="button"
          loading={isLoading}
          iconPlacement="right"
          className="!w-full h-12 !text-base"
          onClick={onSubmit}
        >
          {t('login.submit.web')}
        </Button>
        <Link
          href={EXPERT_ROUTES.RESET_PASSWORD}
          className="text-primary-600 text-sm text-center"
        >
          {t('login.expert.forgot_password')}
        </Link>
      </div>
    </>
  );
};

type LoginMagicLinkViewProps = {
  isLoading: boolean;
  storedEmails: string[];
};

const LoginMagicLinkView: FC<LoginMagicLinkViewProps> = ({
  isLoading,
  storedEmails,
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="my-6">
        <ExpertEmailFieldWithSuggestions
          storedEmails={storedEmails}
          tracing={{ onFocus: WEBAPP_LOGIN_ACCOUNTANT_CLICKEDLOGINEMAILFIELD }}
        />
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <Button
          color="taxes"
          type="submit"
          loading={isLoading}
          iconPlacement="right"
          className="!w-full h-12 !text-base"
        >
          {t('login.submit.web')}
        </Button>
      </div>
    </>
  );
};

const LoginForm: FC = () => {
  const { t } = useTranslation();
  const { instance } = useMsal();
  const [loginMode, setLoginMode] = useState<LoginMode>('methods');
  const navigate = useCustomNavigate();

  const testimonials = useLocalizedTestimonials();

  const storedEmails = getStoredExpertEmails();

  const formAPI = useForm<LoginFormValues>({
    resolver: (values, context, options) => {
      const schema =
        loginMode === 'password'
          ? loginPasswordValidationSchema
          : loginValidationSchema;
      return joiResolver(schema)(values, context, options);
    },
  });
  const { mutate: sendMagicLink, isLoading: isMagicLinkLoading } =
    useExpertSendMagicLink();
  const { onSigninSuccess } = useExpertSigninSuccess({
    redirectToSetPasswordIfNoAuthMethods: true,
  });
  const {
    mutate: signinAccountantMutation,
    isLoading: isPasswordLoginLoading,
  } = useExpertSignin({ ignoreToast: true });
  const { handleSubmit, unregister } = formAPI;

  const switchToMagicMode = () => {
    unregister('password'); // clear password so magic-link submit only has email (validation + API)
    setLoginMode('magic');
  };

  const switchToMethodsView = () => {
    unregister('password'); // clear form state when returning to method selection
    setLoginMode('methods');
  };

  const handleSigninError = (
    error: AxiosError<unknown>,
    mfaState: Record<string, unknown>,
  ) => {
    const handled = handleMfaRequired(error, navigate, mfaState);
    if (!handled) {
      const isGoogleSso = 'googleIdToken' in mfaState;
      const isMicrosoftSso = 'microsoftIdToken' in mfaState;
      const isSso = isGoogleSso || isMicrosoftSso;

      const title = isSso
        ? t('login.expert.sso_login_failed')
        : t('login.errors.failing_access_token_web');

      const provider = isGoogleSso
        ? 'Google'
        : isMicrosoftSso
        ? 'Microsoft'
        : undefined;
      const description = provider
        ? t('login.expert.sso_not_configured', { provider })
        : undefined;

      const passwordActions = !isSso
        ? {
            actions: (themes: { primaryAction: string }) => (
              <div className="mt-1.5">
                <Button
                  asDefault
                  className={themes.primaryAction}
                  onClick={switchToMagicMode}
                >
                  {t('login.expert.switch_to_magic_link')}
                </Button>
              </div>
            ),
          }
        : {};

      toast(
        <AlertV2
          type="error"
          variant="filled"
          size="large-mid"
          renderIcon={() => (
            <ExclamationCircleIconSolid className="w-6 h-6 text-[#DC2626]" />
          )}
          title={title}
          description={description}
          {...passwordActions}
          dismissible
        />,
        toastConfig,
      );
    }
  };

  const handleMagicLink = handleSubmit((values) => {
    storeExpertEmail(values.email);
    tracingEvents.onLogin(values.email, 'magic_link');
    sendMagicLink({ email: values.email });
  });

  const handlePasswordLogin = handleSubmit((values: any) => {
    storeExpertEmail(values.email);
    tracingEvents.onLogin(values.email, 'password');
    signinAccountantMutation(
      {
        email: values.email,
        password: values.password,
        client_id: localStorage.getItem('clientId') || undefined,
      },
      {
        onSuccess: onSigninSuccess,
        onError: (error: unknown) => {
          handleSigninError(error as AxiosError<unknown>, {
            email: values.email,
            password: values.password,
          });
        },
      },
    );
  });

  const handleGoogleLogin = (credential?: string) => {
    if (!credential) return;
    tracingEvents.onLogin(undefined, 'google_sso');
    signinAccountantMutation(
      {
        google_id_token: credential,
        client_id: localStorage.getItem('clientId') || undefined,
      },
      {
        onSuccess: onSigninSuccess,
        onError: (error: unknown) => {
          handleSigninError(error as AxiosError<unknown>, {
            googleIdToken: credential,
          });
        },
      },
    );
  };

  const handleMicrosoftLogin = async () => {
    try {
      const idToken = await getMicrosoftIdToken(instance);
      tracingEvents.onLogin(undefined, 'microsoft_sso');
      signinAccountantMutation(
        {
          microsoft_id_token: idToken,
          client_id: localStorage.getItem('clientId') || undefined,
        },
        {
          onSuccess: onSigninSuccess,
          onError: (error: unknown) => {
            handleSigninError(error as AxiosError<unknown>, {
              microsoftIdToken: idToken,
            });
          },
        },
      );
    } catch (err) {
      console.error(err);
    }
  };

  const subTitle =
    loginMode === 'methods' ? (
      t('login.expert.choose_sign_in_method')
    ) : (
      <>
        <p className="text-primary-600 text-lg text-center">
          {t('login.description.experts.first_line')}
        </p>
        <p className="text-primary-600 text-lg text-center">
          {t('login.description.experts.second_line')}
        </p>
      </>
    );

  return (
    <AuthPageLayout
      title={
        <div className="flex justify-center items-center gap-2 flex-col sm:flex-row">
          <Title
            type={SizesEnum.h5}
            className="text-primary-800 whitespace-nowrap"
          >
            {t('login.title.experts.first_part')}
          </Title>
          <AccountableLogo
            type="wordV2"
            color="#A78AEC"
            className="mb-1 h-[2rem] w-auto"
          />

          <Title
            type={SizesEnum.h5}
            className="text-taxes-400 whitespace-nowrap"
          >
            {t('experts.headerLogo.suffix')}
          </Title>
          <ExpertGlasses aria-hidden="true" size={32} />
        </div>
      }
      subTitle={subTitle}
      renderNavigationBar={() =>
        loginMode !== 'methods' ? (
          <div className="flex items-center justify-between w-full">
            <ExpertAuthBackButton
              className="h-10 !p-2 rounded-lg flex !items-center space-x-2 mb-5 w-fit"
              onClick={switchToMethodsView}
            />
            <ExpertLanguageDropdown />
          </div>
        ) : (
          <div className="flex justify-end w-full">
            <ExpertLanguageDropdown />
          </div>
        )
      }
      renderFooter={() => <ExpertAuthFooter />}
      renderAside={() => <TestimonialsSlider testimonials={testimonials} />}
    >
      <FormProvider {...formAPI}>
        <form
          onSubmit={loginMode === 'magic' ? handleMagicLink : undefined}
          className="flex flex-col min-h-[500px]"
        >
          {loginMode === 'methods' && (
            <LoginMethodsView
              onSelectPassword={() => setLoginMode('password')}
              onSelectMagicLink={switchToMagicMode}
              onGoogleLogin={handleGoogleLogin}
              onMicrosoftLogin={handleMicrosoftLogin}
            />
          )}

          {loginMode === 'password' && (
            <LoginPasswordView
              onSubmit={handlePasswordLogin}
              isLoading={isPasswordLoginLoading}
              storedEmails={storedEmails}
            />
          )}

          {loginMode === 'magic' && (
            <LoginMagicLinkView
              isLoading={isMagicLinkLoading}
              storedEmails={storedEmails}
            />
          )}
        </form>
      </FormProvider>
    </AuthPageLayout>
  );
};

const EmailSentConfirmation: FC<{ email?: string }> = ({ email }) => {
  const { t } = useTranslation();
  const navigate = useCustomNavigate();
  const testimonials = useLocalizedTestimonials();

  const description = t('login.check_inbox_description', { email });
  const search = email ? `${email}.` : '';
  const replacement = email ? `**${email}**.\n\n` : '';
  const markdown = search
    ? description.replace(search, replacement)
    : description;

  return (
    <AuthPageLayout
      title={
        <>
          <RocketArrow aria-hidden="true" />
          <div className="flex justify-center items-center">
            <Title type={SizesEnum.h2} className="text-primary-800">
              {t('login.check_inbox_title')}
            </Title>
          </div>
        </>
      }
      renderNavigationBar={() => (
        <div className="flex items-center justify-between w-full">
          <ExpertAuthBackButton
            className="h-10 !p-2 rounded-lg flex !items-center space-x-2 mb-5 w-fit"
            onClick={() => navigate(-1 as any, { asDefault: true })}
          />
          <ExpertLanguageDropdown />
        </div>
      )}
      subTitle={
        email ? (
          <ReactMarkdown
            components={{
              p: ({ children }) => (
                <p className="whitespace-pre-wrap">{children}</p>
              ),
            }}
          >
            {markdown}
          </ReactMarkdown>
        ) : (
          description
        )
      }
      renderFooter={() => <ExpertAuthFooter />}
      renderAside={() => <TestimonialsSlider testimonials={testimonials} />}
    ></AuthPageLayout>
  );
};

const ExpertLogin: FC = () => {
  const { search } = useLocation();
  const { state, email = '' } = queryString.parse(search);

  const sanitizedEmail =
    typeof email == 'string' && email?.match(emailRegex) ? email : 'email';

  return state === 'sent' ? (
    <EmailSentConfirmation email={sanitizedEmail} />
  ) : (
    <LoginForm />
  );
};

export default ExpertLogin;
